import { useMemo } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { ApacCheckoutComponent } from "@apac/checkout";
import { BirthDateField, useCountryOptions } from "@apac/core-travelers";
import { TextField } from "@apac/form-ui";
import { SelectField } from "@apac/form-ui";
import { useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Gender } from "@hopper-b2b/types";
import styles from "./styles.module.scss";

interface TravelerDetailsProps {
  formContext: UseFormReturn<FieldValues, unknown, undefined>;
  showNationality?: boolean;
  dateFormatter?: (date: string) => string;
}

export const TravelerDetails = ({
  formContext,
  showNationality = true,
  dateFormatter,
}: TravelerDetailsProps) => {
  const [block, cn] = useModuleBEM(
    styles,
    ApacCheckoutComponent.TravellerDetails
  );
  const { t } = useI18nContext();

  const genderOptions = useMemo(
    () => [
      { label: t("person_male"), value: Gender.M },
      { label: t("person_female"), value: Gender.F },
    ],
    [t]
  );

  const countryOptions = useCountryOptions();

  return (
    <div className={block}>
      <TextField
        className={cn("first-name")}
        formContext={formContext}
        name="firstName"
        label={`${t("firstName")}*`}
        placeholder={t(
          "checkout.travelerInformation.travelerForm.firstNamePlaceholder"
        )}
        required
      />
      <TextField
        className={cn("middle-name")}
        formContext={formContext}
        name="middleName"
        label={t("middleNameOptional")}
        placeholder={t(
          "checkout.travelerInformation.travelerForm.middleNamePlaceholder"
        )}
      />
      <TextField
        className={cn("last-name")}
        formContext={formContext}
        name="lastName"
        label={`${t("lastName")}*`}
        placeholder={t(
          "checkout.travelerInformation.travelerForm.lastNamePlaceholder"
        )}
        required
      />
      <BirthDateField
        className={cn("date-of-birth")}
        formContext={formContext}
        name="dateOfBirth"
        label={`${t("checkout.travelerInformation.travelerForm.dateOfBirth")}*`}
        placeholder={t(
          "checkout.travelerInformation.travelerForm.dateOfBirthPlaceholder"
        )}
        dateFormatter={dateFormatter}
      />
      <SelectField
        formContext={formContext}
        name="gender"
        label={`${t("gender")}*`}
        options={genderOptions}
        placeholder={t("choose")}
      />
      {showNationality && (
        <SelectField
          formContext={formContext}
          name="nationality"
          label={`${t("nationality")}*`}
          options={countryOptions}
          placeholder={t("choose")}
        />
      )}
    </div>
  );
};
